import React, { useState, useEffect } from 'react';
import { Drawer, List, Checkbox, Row, Col, Divider, Button, Select, message } from 'antd';
import userService from '../../services/userService';
import sensorboxGroupService from '../../services/sensorboxGroupService';
import assetGroupService from '../../services/assetGroupService';
import constants from '../../config/constants';
import authStore from '../../store/authStore';

import {
    RightOutlined,
    CheckCircleFilled
} from '@ant-design/icons';

interface Props {
    data?: any,
    visible?: any,
    orgid?: any
    onCloseDrawer: () => void,
    onApplyFilter: (values: Array<object>) => void,
    onClearAll: () => void
}

const { Option } = Select;

const ISSUE_TRACKER_FILTER_LIST_LIMIT = 500

const FILTER_STATUS = 'Status'
const FILTER_ORG_ASSET_GROUP = 'Org / Asset Group'
const FILTER_ASSET = 'Asset'
const FILTER_USER = 'User'
const FILTER_CONTRACTOR = 'Contractor'
const FILTER_RESOLVED_BY = 'Resolved By'

const IssueTrackerFilterDrawer: React.FC<Props> = ({ data, visible, onCloseDrawer, orgid, onApplyFilter, onClearAll }) => {

    const [activeFilterCategory, setActiveFilterCategory] = useState('')
    const [users, setUsers] = useState<any>([]);
    const [sensorboxGroups, setSensorboxGroups] = useState<any[]>([]);
    const [assetGroups, setAssetGroups] = useState<any[]>([]);
    const [filterData, setFilterData] = useState<any>({});

    useEffect(() => {
        setFilterData(data)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, visible])

    const filterSubjects = [FILTER_STATUS, FILTER_ORG_ASSET_GROUP, FILTER_ASSET, FILTER_USER, FILTER_CONTRACTOR, FILTER_RESOLVED_BY]
    const contractorFilterSubjects = [FILTER_STATUS, FILTER_ORG_ASSET_GROUP, FILTER_ASSET, FILTER_USER, FILTER_RESOLVED_BY]

    const ticketStatus = [{ status: 'OPEN', title: 'Open' }, { status: 'RESOLVED', title: 'Resolved' }, { status: 'CLOSED', title: 'Closed' }, { status: 'IGNORED', title: 'Ignored' }, { status: 'MISSED', title: 'Missed' }, { status: 'PENDING REVIEW', title: 'Pending Review' }, { status: 'INCOMPLETE', title: 'Incomplete' }]

    const loadSensorboxGroups = async () => {
        try {
            const params: Record<string, string> = { page: '1', limit: String(ISSUE_TRACKER_FILTER_LIST_LIMIT) }
            if (orgid) params.orgid = String(orgid)
            const res = await sensorboxGroupService.getSensorboxGroups(params)
            setSensorboxGroups(res.data?.groups || [])
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to load sensorbox groups')
            setSensorboxGroups([])
        }
    }

    const loadAssetGroups = async () => {
        try {
            const params: Record<string, string> = { page: '1', limit: String(ISSUE_TRACKER_FILTER_LIST_LIMIT) }
            if (orgid) params.orgid = String(orgid)
            const res = await assetGroupService.getAssetGroups(params)
            setAssetGroups(res.data?.assetGroups || [])
        } catch (e: any) {
            message.error(e?.response?.data?.message || 'Failed to load asset groups')
            setAssetGroups([])
        }
    }

    const onFilterCategorySelect = (filterCategory: string) => {
        setActiveFilterCategory(filterCategory)

        if (filterCategory === FILTER_ASSET) {
            loadSensorboxGroups()
            return
        }
        if (filterCategory === FILTER_ORG_ASSET_GROUP) {
            loadAssetGroups()
            return
        }

        let role = 0;
        if (filterCategory === FILTER_CONTRACTOR) {
            role = 2
        } else if (filterCategory === FILTER_RESOLVED_BY) {
            role = 1
        }

        getUsers(role, orgid)
    }

    const getUsers = async (role?: any, org_id?: any) => {

        const userList = await userService.getUserBasedOnRoleType(org_id, role)
        setUsers(userList)
    }

    const onSensorboxGroupSelect = (_id: string | undefined) => {
        const data = { ...filterData };
        if (!_id) {
            delete data.sensorbox_group;
        } else {
            const g = sensorboxGroups.find((item: any) => String(item?._id) === String(_id));
            if (g) {
                data.sensorbox_group = [{ _id: g._id, name: g.name }];
            } else {
                delete data.sensorbox_group;
            }
        }
        setFilterData(data);
    }

    const onAssetGroupSelect = (_id: string | undefined) => {
        const data = { ...filterData };
        if (!_id) {
            delete data.asset_group;
        } else {
            const g = assetGroups.find((item: any) => String(item?._id) === String(_id));
            if (g) {
                data.asset_group = [{
                    _id: g._id,
                    name: g.name,
                    assets: g.assets,
                    sensorboxes: g.sensorboxes,
                }]
            } else {
                delete data.asset_group;
            }
        }
        setFilterData(data);
    }

    const onApplyHandler = () => {
        setActiveFilterCategory('')
        onApplyFilter(filterData)
    }

    const handleClearAll = () => {
        setActiveFilterCategory('')
        setFilterData({})
        onClearAll()
    }

    const handleCloseDrawer = () => {
        setActiveFilterCategory('')
        setFilterData({})
        onCloseDrawer()
    }

    const isActive = (item: any) => {
        let isActive = false
        if (item === FILTER_STATUS) {
            isActive = (filterData?.status?.length > 0)
        } else if (item === FILTER_USER) {
            isActive = (filterData?.consumer?.length > 0)
        } else if (item === FILTER_CONTRACTOR) {
            isActive = (filterData?.contractor?.length > 0)
        } else if (item === FILTER_RESOLVED_BY) {
            isActive = (filterData?.service_resolved_by?.length > 0)
        } else if (item === FILTER_ASSET) {
            isActive = (filterData?.sensorbox_group?.length > 0)
        } else if (item === FILTER_ORG_ASSET_GROUP) {
            isActive = (filterData?.asset_group?.length > 0)
        }

        return isActive
    }

    const onFilterChange = (filterCategory: string, value: Array<string>) => {
        const data = { ...filterData };
        let user = users?.find((user: any) => user?._id === value)
        data[filterCategory] = filterCategory === 'status' ? value : [user]
        setFilterData(data);
    }

    return (
        <>
            <Drawer title="Filter Ticket" placement="right" onClose={handleCloseDrawer} visible={visible}>
                {
                    activeFilterCategory === '' &&
                    <List
                        className='font-weight-bold pointer'
                        dataSource={authStore?.currentUser?.active_role?.role_type === constants.role.Contractor ? contractorFilterSubjects : filterSubjects}
                        renderItem={(item) => <List.Item onClick={() =>
                            onFilterCategorySelect(item)} >{item}
                            {isActive(item) && <div className='d-inline ml-2'><CheckCircleFilled /></div>}
                            <div className='float-right'><RightOutlined /></div></List.Item>
                        }
                    />
                }
                {
                    activeFilterCategory === FILTER_STATUS &&
                    <div>

                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6
                                className="mr-4 font-weight-bold"
                            >{`< Back `}
                            </h6>
                        </a>
                        <div className='issue-tracker-filter'>
                            <Checkbox.Group value={filterData?.status} onChange={(value: any) => { onFilterChange('status', value) }}>
                                <Row>
                                    {
                                        ticketStatus?.map((status: any) =>
                                            <Col span={24}>
                                                <p className='mt-2 paragraph'><Checkbox value={status?.status}>{status?.title}</Checkbox></p>
                                            </Col>
                                        )
                                    }
                                </Row>
                            </Checkbox.Group>
                        </div>

                    </div>
                }
                {
                    activeFilterCategory === FILTER_USER &&
                    <div>
                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6
                                className="mr-4 font-weight-bold"
                            >{`< Back `}
                            </h6>
                        </a>

                        <p className='font-weight-bold mt-3'>{FILTER_USER}</p>
                        <Col span={24}>
                            <Select value={filterData?.consumer ? filterData?. consumer[0]._id : null} placeholder="Choose" style={{ minWidth: "100%" }} onChange={(value: any) => { onFilterChange('consumer', value) }} optionFilterProp="children" showSearch>
                                {
                                    users &&
                                    users.map((user: any) => {
                                        return (
                                            <Option value={user?._id}>{user.name}</Option>
                                        );
                                    })
                                }
                            </Select>
                        </Col>


                    </div>
                }
                {
                    activeFilterCategory === FILTER_CONTRACTOR &&
                    <div>
                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6
                                className="mr-4 font-weight-bold"
                            >{`< Back `}
                            </h6>
                        </a>

                        <p className='font-weight-bold mt-3'>Contractor</p>

                        <Col span={24}>
                            <Select value={filterData?.contractor ? filterData?.contractor[0]?._id : null} placeholder="Choose" style={{ minWidth: "100%" }} onChange={(value: any) => { onFilterChange('contractor', value) }} optionFilterProp="children" showSearch>
                                {
                                    users &&
                                    users.map((user: any) => {
                                        return (
                                            <Option value={user?._id}>{user.name}</Option>
                                        );
                                    })
                                }
                            </Select>
                        </Col>

                    </div>

                }
                {
                    activeFilterCategory === FILTER_ASSET &&
                    <div>
                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6 className="mr-4 font-weight-bold">{`< Back `}</h6>
                        </a>
                        <p className='font-weight-bold mt-3'>{FILTER_ASSET}</p>
                        <Col span={24}>
                            <Select value={filterData?.sensorbox_group ? filterData?.sensorbox_group[0]?._id : undefined}
                                placeholder="Choose" allowClear style={{ minWidth: "100%" }}
                                onChange={(value: string | undefined) => { onSensorboxGroupSelect(value); }}
                                optionFilterProp="children" showSearch>
                                {(sensorboxGroups || []).map((g: any) => (
                                    <Option key={g?._id} value={g?._id}>{g?.name}</Option>
                                ))}
                            </Select>
                        </Col>
                    </div>
                }
                {
                    activeFilterCategory === FILTER_ORG_ASSET_GROUP &&
                    <div>
                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6 className="mr-4 font-weight-bold">{`< Back `}</h6>
                        </a>
                        <p className='font-weight-bold mt-3'>{FILTER_ORG_ASSET_GROUP}</p>
                        <Col span={24}>
                            <Select value={filterData?.asset_group ? filterData?.asset_group[0]?._id : undefined}
                                placeholder="Choose" allowClear style={{ minWidth: "100%" }}
                                onChange={(value: string | undefined) => { onAssetGroupSelect(value); }}
                                optionFilterProp="children" showSearch>
                                {(assetGroups || []).map((g: any) => (
                                    <Option key={g?._id} value={g?._id}>{g?.name}</Option>
                                ))}
                            </Select>
                        </Col>
                    </div>
                }
                {
                    activeFilterCategory === FILTER_RESOLVED_BY &&
                    <div>
                        <a href="/" className="ins_edit_icn" onClick={(e: any) => { setActiveFilterCategory(''); e.preventDefault(); }}>
                            <span className="sr-only"></span>
                            <h6
                                className="mr-4 font-weight-bold"
                            >{`< Back `}
                            </h6>
                        </a>

                        <p className='font-weight-bold mt-3'>Resolved By</p>

                        <Col span={24}>
                            <Select value={filterData?.service_resolved_by ? filterData?.service_resolved_by[0]?._id : null} placeholder="Choose" style={{ minWidth: "100%" }} onChange={(value: any) => { onFilterChange('service_resolved_by', value) }} optionFilterProp="children" showSearch>
                                {
                                    users &&
                                    users.map((user: any) => {
                                        return (
                                            <Option value={user?._id}>{user.name}</Option>
                                        );
                                    })
                                }
                            </Select>
                        </Col>

                    </div>
                }

                <div className='mt-3'>
                    <Divider
                        type="horizontal"
                        style={{ backgroundColor: "#000", margin: "auto" }}
                    />

                    <Col span={24}>
                        <div className="text-right mt-4">
                            <Button className="text-left" type="text" onClick={handleClearAll}>Clear All</Button>
                            <Button className="h-36 py-0 pl-4 pr-4 fs-14 border-0 font-weight-bold mr-3" type="primary" style={{ backgroundColor: "#4F8C92", borderColor: "#4F8C92" }} onClick={onApplyHandler}>Apply</Button>
                        </div>

                    </Col>
                </div>
            </Drawer>
        </>
    );
}
export default IssueTrackerFilterDrawer;